import React, { useEffect, useState } from "react";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ReferenceLine, ReferenceArea, CartesianGrid } from "recharts";
import { formatCOP } from "../../lib/apiClient";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { BudgetEditor } from "./BudgetEditor";

const ALERT_PCT = 50;
const short = (v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)} mil M` : v >= 1e6 ? `${Math.round(v / 1e6)} M` : `${Math.round(v / 1e3)} mil`);
const quantile = (s, p) => {
  const pos = (s.length - 1) * p, i = Math.floor(pos);
  return s[i + 1] !== undefined ? s[i] + (s[i + 1] - s[i]) * (pos - i) : s[i];
};

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 p-2 text-xs shadow-sm max-w-[260px]">
      <p className="font-mono text-gray-900">{formatCOP(p.valor)}</p>
      <p className="text-gray-600 line-clamp-2">{p.entidad}</p>
    </div>
  );
}

function BudgetAlert({ diff, median, range, budget, onSaveBudget }) {
  const above = diff > 0;
  return (
    <div className="mb-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3" data-testid="budget-alert">
      <div className="flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
          <strong>Revise su presupuesto.</strong> Está {Math.abs(diff)}% {above ? "por encima" : "por debajo"} de la mediana del mercado ({formatCOP(median)}).{" "}
          {above ? "Puede estar sobreestimado o el alcance ser distinto al de los comparables." : "Puede resultar insuficiente y dejar el proceso desierto."}
        </span>
      </div>
      {onSaveBudget && <BudgetEditor budget={budget} range={range} onSave={onSaveBudget} />}
    </div>
  );
}

export function MarketSummaryChart({ comparables = [], budget, onSaveBudget }) {
  const [b, setB] = useState(Number(budget) || null);
  const [justFixed, setJustFixed] = useState(false);
  useEffect(() => { setB(Number(budget) || null); }, [budget]);
  const pts = comparables.filter((c) => c.valor_cop > 0).map((c, i) => ({ x: i + 1, valor: Number(c.valor_cop), entidad: c.entidad }));
  if (!pts.length) return null;
  const vals = pts.map((p) => p.valor).sort((x, y) => x - y);
  const median = quantile(vals, 0.5);
  const range = { low: quantile(vals, 0.25), high: quantile(vals, 0.75) };
  const lo = Math.min(vals[0], b || vals[0]) * 0.6;
  const hi = Math.max(vals[vals.length - 1], b || 0) * 1.6;
  const diff = b ? Math.round(((b - median) / median) * 100) : null;
  const save = onSaveBudget && (async (v) => { await onSaveBudget(v); setB(v); setJustFixed(true); });
  return (
    <div className="mb-4 border border-gray-200 p-4" data-testid="market-summary-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-gray-900">Resumen del mercado</p>
        <p className="text-xs text-gray-600" data-testid="market-summary-stats">
          {pts.length} contrato(s) · mediana {formatCOP(median)}
          {b && <> · su presupuesto <strong data-testid="market-summary-budget">{formatCOP(b)}</strong> ({diff > 0 ? "+" : ""}{diff}% vs. mediana)</>}
        </p>
      </div>
      <p className="text-xs text-emerald-800 mb-2" data-testid="market-summary-range">
        Rango de referencia: <strong>{formatCOP(range.low)}</strong> a <strong>{formatCOP(range.high)}</strong> (mitad central de los contratos comparables)
      </p>
      {diff !== null && Math.abs(diff) > ALERT_PCT && <BudgetAlert diff={diff} median={median} range={range} budget={b} onSaveBudget={save} />}
      {justFixed && Math.abs(diff) <= ALERT_PCT && (
        <p className="mb-3 text-xs text-emerald-800 flex items-center gap-1.5" data-testid="budget-fixed-ok">
          <CheckCircle2 className="w-4 h-4" /> Presupuesto actualizado en su borrador; ahora está dentro de lo esperado frente al mercado.
        </p>
      )}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
            <XAxis type="number" dataKey="x" domain={[0, pts.length + 1]} hide />
            <YAxis type="number" dataKey="valor" scale="log" domain={[lo, hi]} allowDataOverflow tickFormatter={short} tick={{ fontSize: 11, fill: "#6b7280" }} width={70} />
            <ZAxis range={[80, 80]} />
            <Tooltip content={<Tip />} cursor={false} />
            <ReferenceArea y1={range.low} y2={Math.max(range.high, range.low * 1.02)} fill="#10b981" fillOpacity={0.12} stroke="none" />
            <ReferenceLine y={median} stroke="#9ca3af" strokeDasharray="4 4" label={{ value: "Mediana", position: "insideBottomLeft", fontSize: 10, fill: "#6b7280" }} />
            {b && <ReferenceLine y={b} stroke="hsl(var(--primary))" strokeWidth={2} label={{ value: "Su presupuesto", position: "insideTopRight", fontSize: 11, fill: "hsl(var(--primary))" }} />}
            <Scatter data={pts} fill="#334155" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-gray-500 mt-1">Cada punto es un contrato SECOP comparable (escala logarítmica); la franja verde es el rango de referencia. {!b && "No registró presupuesto estimado en el formulario."}</p>
    </div>
  );
}
